// Copyright (c) Docugami, Inc. All rights reserved.

import { DirectionalHint, TooltipHost } from "@fluentui/react";
import { ToggleCommand, ToggleCommandBar } from "components/reports/ToggleCommandBar";
import { chunkHintColor, emptyGray, lightHighlightBlue, machineGreen, primaryTheme } from "components/shell/presentation/AppStyles";
import { dgIconNames, useDgIcons } from "hooks/dgIcons";
import { ReportBinding } from "networking/reportsApi";
import { MouseEvent, ReactNode, useMemo } from "react";
import styled from "styled-components";
import { AutomationProps } from "utils/Automation";
import { cardShadow, getClassNames } from "./CardStyles";
import { ICommand, ICommandButton } from "./CommandButton";
import { ItemSlotCommandBar } from "./ItemSlotCommandBar";
import { ItemSlotMarker } from "./ItemSlotMarker";

export type ItemSlotStyles = {
    root?: string[];
}

export const UnconfirmedClassName = "dashedBorder";
export const SelectableClassName = "selectable";
export const BusyClassName = "busy";
export const DisabledClassName = "disabled";
export const LinkableClassName = "bindable";
export const ActiveDropTargetStyle = "dropTarget";
export const HighlightedClassName = "highlight";

// ItemSlot child class names
export const ShowOnSlotHoverClassName = 'show-on-hover';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`

const SlotRoot = styled.div.attrs(props => ({ className: props.className }))`
    display: flex;
    flex-direction: row;
    border-radius: 5px;
    background: ${primaryTheme.palette.white};
    box-shadow: ${cardShadow};
    margin: 5px; /* Account for box shadow */
    &.dashedBorder {
        border: 1px dashed ${emptyGray};
    }
    &.suggestion {
        border-color: ${machineGreen};
    }
    &.selectable {
        cursor: pointer;
        :hover {
            transform: scale(1.01);
        }
    }
    &.highlight {
        background: ${lightHighlightBlue};
    }
    &.busy {
        cursor: wait;
        * {
            opacity: .8;
            pointer-events: none;
        }
    }
    &.disabled {
        opacity: 25%;
        pointer-events: none;
    }
    &.bindable:hover, &.dropTarget {
        background: ${chunkHintColor};
    }
    overflow: hidden;

    .show-on-hover {
        display: none;
    }
    :hover {
        .show-on-hover {
            display: inherit;
        }
    }
`

const SlotContent = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    overflow: hidden;
`

const Body = styled.div`
    display: flex;
    flex-direction: row;
    flex: 1;
    box-sizing: border-box;
`

const WarningContainer = styled.div.attrs({
    'data-componentname': 'WarningContainer'
})`
    width: 15px;
    height: 100%;
    background: #FEC705;
    align-items: center;
    display: flex;
    justify-content: center;
`;

const Footer = styled.div`
    display: flex;
`

const ContentRoot = styled.div<{ maxHeight?: number }>`
    flex: 1;
    max-height: ${props => props.maxHeight !== undefined ? `${props.maxHeight}px` : '100px'};
    overflow: hidden;
    gap: 5px;
    padding: 8px 4px;
`

const SlotMarkersContainer = styled.div`
    display: flex;
    flex-direction: column;
    overflow: hidden;
    justify-content: center;
    margin-left: 3px;
`;

const SlotCommands = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
`;

export enum SlotItemType {
    Chunk = "Chunk",
    ReportChunk = "ReportChunk",
    LabelChunk = "LabelChunk",
    PropertyChunk = "PropertyChunk",
}

export const isChunkSlot = (info: ISlotItem): info is ChunkSlotItem => info.type === SlotItemType.Chunk;
export const isReportChunkSlot = (info: ISlotItem): info is ReportChunkSlotItem => info.type === SlotItemType.ReportChunk;
export const isLabelChunkSlot = (info: ISlotItem): info is LabelChunkSlotItem => info.type === SlotItemType.LabelChunk;
export const isPropertyChunkSlot = (info: ISlotItem): info is PropertyChunkSlotItem => info.type === SlotItemType.PropertyChunk;

export interface ChunkSlotItem extends ISlotItem {
    type: SlotItemType.Chunk;
    isDisabled?: boolean;
    spanId?: string;
}

export interface ReportChunkSlotItem extends ISlotItem {
    type: SlotItemType.ReportChunk,
    isDisabled?: boolean;
    isLinkable?: boolean;
    entryId: string;
    binding?: ReportBinding;
    isHighlighted?: boolean;
}

export interface LabelChunkSlotItem extends ISlotItem {
    type: SlotItemType.LabelChunk;
    label: string;
    labelId?: string;
    spanId: string;
    isSuggestion?: boolean;
}

export interface PropertyChunkSlotItem extends ISlotItem {
    type: SlotItemType.PropertyChunk;
}

export interface ISlotCommands {
    confirm: ICommand;
    clear: ICommand;
}

export interface ISlotItem {
    id: string;
    type: SlotItemType;
}

export interface IItemSlotMarker {
    label: string;
    icon: ReactNode;
    color: string;
    tooltip?: string;
}

export interface ItemSlotProps extends AutomationProps {
    id?: string;
    showCommandsList?: boolean;
    children?: JSX.Element | JSX.Element[];
    footer?: JSX.Element;
    commands?: ICommandButton[];
    isConfirmed?: boolean;
    onClick?: () => void;
    onDoubleClick?: () => void;
    styles?: ItemSlotStyles;
    maxHeight?: number;
    hideManagementCommands?: boolean;
    markers?: IItemSlotMarker[];
    toggles?: ToggleCommand[];
    warning?: string;
    minCommandBarItems?: number;
}

export const ItemSlot = (props: ItemSlotProps): JSX.Element => {
    const { icon } = useDgIcons();
    const sortedItems = useMemo(() => props.commands?.reduce((prev, value) => {
        if (value.isOverflow) {
            prev.overflowItems.push(value);
        } else {
            prev.items.push(value);
        }

        return prev;
    }, { items: [], overflowItems: [] } as { items: ICommandButton[], overflowItems: ICommandButton[] }), [props.commands]);

    function onClick(ev: MouseEvent) {
        ev?.stopPropagation();
        props.onClick?.();
    }

    return (
        <Container
            id={createItemSlotElementId(props.id)}
            data-automation-id={props.automationId}>
            <SlotRoot className={getClassNames(props.styles?.root)}>
                <ToggleCommandBar toggles={props.toggles} hidden={props.hideManagementCommands} />
                {
                    !!props.markers && props.markers.length > 0 ? (
                        <SlotMarkersContainer>
                            {
                                props.markers.map(x => <ItemSlotMarker key={x.label} info={x} />)
                            }
                        </SlotMarkersContainer>
                    ) : !!props.warning && !props.isConfirmed && (
                        <TooltipHost content={props.warning} directionalHint={DirectionalHint.leftCenter}>
                            <WarningContainer> {
                                icon(
                                    dgIconNames.DgBang,
                                    '',
                                    16,
                                    6,
                                    primaryTheme.palette.white,
                                    '')
                            } </WarningContainer>
                        </TooltipHost>
                    )
                }
                <SlotContent onClick={onClick} onDoubleClick={props.onDoubleClick}>
                    <Body>
                        <ContentRoot maxHeight={props.maxHeight} >
                            {props.children}
                        </ContentRoot>
                    </Body>
                    <Footer>
                        {props.footer}
                    </Footer>
                </SlotContent>
                <SlotCommands>
                    <ItemSlotCommandBar
                        showOnHover={!props.showCommandsList}
                        commands={sortedItems?.items}
                        overflowCommands={sortedItems?.overflowItems}
                        minItems={props.minCommandBarItems} />
                </SlotCommands>
            </SlotRoot>
        </Container>
    );
}

export function createItemSlotElementId(id?: string): string {
    if (!id) {
        return '';
    }

    return `itemSlot_${id}`;
}
